import moment from "moment"
import { array, boolean, date, mixed, number, object, string } from "yup"
import { flexPurchaseProduct } from "../lib"

const integrationParameterSchema = object({
  id: number().nullable(),
  name: string().required("required"),
  value: string().required("required"),
  _destroy: boolean()
})

export const getPurchaseProductSchema = (user) => {
  if (user.isAuthorizedOfficer) {
    return purchaseProductSchemaWithPurchasePrice
  }

  return purchaseProductSchema
}

export const purchaseProductSchema = object({
  supplier_id: number().required("required"),
  name: string().required("required"),
  purchase_type: mixed()
    .transform((value) => value?.id)
    .oneOf([1, 2, 3, 4, 5])
    .required("required"),
  runtime_in_days: number()
    .transform((value, originalValue) => {
      return originalValue === "" || originalValue === undefined
        ? null
        : Number(originalValue)
    })
    .when("purchase_type", {
      is: (purchase_type) => flexPurchaseProduct(purchase_type),
      then: number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue === undefined
            ? null
            : Number(originalValue)
        })
        .nullable(),
      otherwise: number()
        .transform((value, originalValue) => {
          return originalValue === "" || originalValue === undefined
            ? null
            : Number(originalValue)
        })
        .min(0)
        .required("required")
    }),
  rrp: string().when("purchase_type", {
    is: (purchase_type) => flexPurchaseProduct(purchase_type),
    then: string().price("price").nullable(),
    otherwise: string().price("price").required("required")
  }),
  stock_quantity: number().min(0).required("required"),
  stock_expires_at: date()
    .typeError("invalid-date")
    .nullable()
    .min(moment().startOf("day").add(1, "days").toDate(), "date-in-past"),
  is_free: boolean(),
  schalttool: mixed().transform((value) => value?.id),
  purchase_price_discount_percent: number()
    .integer("Must be an integer")
    .max(100, "Must not be greater than 100")
    .when("purchase_type", {
      is: (purchase_type) => flexPurchaseProduct(purchase_type),
      then: number().required("required"),
      otherwise: number().nullable()
    }),
  integration_parameters_attributes: array().of(integrationParameterSchema)
})

export const purchaseProductSchemaWithPurchasePrice =
  purchaseProductSchema.concat(
    object({
      purchase_price: string().when("purchase_type", {
        is: (purchase_type) => flexPurchaseProduct(purchase_type),
        then: string().nullable(),
        otherwise: string().price("price").required("required")
      })
    })
  )

export const purchaseProductTransformSchema = object({
  supplier_id: number(),
  name: string(),
  purchase_type: mixed().transform((value) => value?.id),
  runtime_in_days: number().transform((value) => {
    return value === "" || value === undefined || isNaN(value)
      ? 0
      : Number(value)
  }),
  rrp: string(),
  purchase_price: string(),
  stock_quantity: number(),
  stock_expires_at: string().transform((value) =>
    moment(value).format("YYYY-MM-DD")
  ),
  is_free: boolean(),
  schalttool: mixed().transform((value) => value?.id ?? null),
  purchase_price_discount_percent: number()
    .emptyToNull()
    .integer("Must be an integer")
    .max(100, "Must not be greater than 100")
    .min(0, "non-negative"),
  integration_parameters_attributes: array().of(integrationParameterSchema)
})

export const purchasePriceChangesSchema = object({
  new_purchase_price: number().emptyToNull().min(0, "non-negative"),
  new_rrp: number().emptyToNull().min(0, "non-negative"),
  new_stock_expires_at: date().typeError("invalid-date").nullable(),
  valid_from: date().typeError("invalid-date").nullable().required("required"),
  activate: boolean(),
  sales_products: array().of(
    object({
      id: number(),
      new_rrp: number().emptyToNull().min(0, "non-negative"),
      new_seller_lower_limit: number().emptyToNull().min(0, "non-negative"),
      new_tl_price: number().emptyToNull().min(0, "non-negative"),
      new_price_in_package: number().emptyToNull().min(0, "non-negative"),
      new_min_price_in_package: number().emptyToNull().min(0, "non-negative"),
      product_prices: array().of(
        object({
          id: number(),
          new_market_price: number().emptyToNull().min(0, "non-negative"),
          new_price: number().emptyToNull().min(0, "non-negative"),
          new_lower_limit: number().emptyToNull().min(0, "non-negative")
        })
      )
    })
  )
})
